import {KeyVault, KeyVaultRef, Role} from "../../api/Model";
import {Logger} from "../Logger";
import {roleAsString} from "./onRoleChange";

type VaultAccess =
    | { type: 'none' }
    | { type: 'some', vaultIds: ReadonlySet<KeyVaultRef['id']> }
    | { type: 'all' }
    ;

export class AccessibleVaults {
    private readonly vaultAccess: VaultAccess;

    constructor(private readonly officeRoles: readonly Role[]) {
        this.vaultAccess = AccessibleVaults.accessibleVaults(officeRoles);
    }

    canAccessSingleVault(): boolean {
        return this.vaultAccess.type === 'some' && this.vaultAccess.vaultIds.size === 1;
    }

    getDefaultKeyVault(keyVaults: KeyVault[]) {
        const logger = Logger('getDefaultKeyVault', {withId: true});
        logger.debug(`for ${roleAsString(this.officeRoles)}, from vaults: ${keyVaults.map(keyVault => keyVault.name).join(', ')}`);
        switch (this.vaultAccess.type) {
            case 'none':
                logger.debug(`  -> no accessible key vaults found`);
                throw new Error('No key vaults accessible');
            case 'some':
                const vaultIds = this.vaultAccess.vaultIds;
                const managedKeyVault = keyVaults
                    .find(keyVault => vaultIds.has(keyVault.id));
                if (managedKeyVault) {
                    logger.debug(`  -> the managed one: ${managedKeyVault.name}`);
                    return managedKeyVault;
                } else {
                    logger.debug(`  -> the managed one not found`);
                    throw new Error(`Cannot find managed KeyVault with IDs ${vaultIds}`);
                }
            case 'all':
                if (keyVaults.length > 0) {
                    logger.debug(`  -> the first one: ${keyVaults[0].name}`);
                    return keyVaults[0];
                } else {
                    logger.debug(`  -> no key vaults found`);
                    throw new Error('No key vaults found');
                }
        }
    }

    private static none: VaultAccess = {type: 'none'} as const;
    private static all: VaultAccess = {type: 'all'} as const;

    private static accessibleVaults(officeRoles: readonly Role[]): VaultAccess {
        return officeRoles
            .reduce((vaultAccess: VaultAccess, role) => {
                switch (role.type) {
                    case "guest":
                        return vaultAccess;
                    case "office-admin":
                    case "system-admin":
                        return AccessibleVaults.all;
                    case "vault-manager":
                        switch (vaultAccess.type) {
                            case "none":
                                return {
                                    type: 'some',
                                    vaultIds: new Set([role.vaultId]),
                                };
                            case "some":
                                return {
                                    type: 'some',
                                    vaultIds: new Set([
                                        ...vaultAccess.vaultIds,
                                        role.vaultId,
                                    ]),
                                };
                            case "all":
                                return AccessibleVaults.all;
                        }
                }
                return vaultAccess;
            }, AccessibleVaults.none);
    }
}